import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import VoiceInputButton from '@/components/feature/VoiceInputButton';
import { insertTranscriptAtCursor } from '@/lib/voiceInsert';
import {
  createCoreNote,
  listCoreNotes,
  noteVisibilityMeta,
  softDeleteCoreNote,
  toggleNotePin,
  updateCoreNote,
  type CoreNote,
} from '@/lib/coreNotes';

interface Props {
  organizationId: number;
  tenantId: number | null;
  entityType: string;
  entityId: string | number;
  actorName?: string | null;
  actorUserId?: number | null;
}

const INPUT =
  'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

function fmt(d: string | null): string {
  if (!d) return '';
  return new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function EntityNotesPanel({
  organizationId,
  tenantId,
  entityType,
  entityId,
  actorName,
  actorUserId,
}: Props) {
  const [notes, setNotes] = useState<CoreNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [visibility, setVisibility] = useState('internal');
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  // Dictée vocale : insertion dans le corps de la note (utilisateurs connectés).
  const { user } = useAuth();
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const handleTranscript = useCallback((text: string) => {
    setBody((prev) => insertTranscriptAtCursor(bodyRef.current, prev, text));
    bodyRef.current?.focus();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setNotes(await listCoreNotes(organizationId, { entityType, entityId }));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [organizationId, entityType, entityId]);

  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    if (!body.trim()) return;
    setSaving(true);
    try {
      await createCoreNote({
        tenantId,
        organizationId,
        entityType,
        entityId: String(entityId),
        title: title.trim(),
        body: body.trim(),
        visibility,
        authorName: actorName ?? null,
        authorUserId: actorUserId ?? null,
      });
      setTitle('');
      setBody('');
      setVisibility('internal');
      setOpen(false);
      load();
    } finally {
      setSaving(false);
    }
  };

  const pin = async (n: CoreNote) => {
    setBusyId(n.id);
    try {
      await updateCoreNote(n.id, { pinned: !n.pinned });
      load();
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (n: CoreNote) => {
    setBusyId(n.id);
    try {
      await softDeleteCoreNote(n.id, actorUserId ?? null);
      load();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="rounded-lg border border-background-200/70 bg-background-50 overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-background-200/60">
        <div className="flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center">
            <i className="ri-sticky-note-line"></i>
          </span>
          <div>
            <h4 className="text-sm font-semibold text-foreground-900">Notes internes</h4>
            <p className="text-[11px] text-foreground-400">Les notes privées ne sont jamais exposées à l&apos;AI Client</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
        >
          <i className={open ? 'ri-close-line' : 'ri-add-line'}></i>
          {open ? 'Annuler' : 'Nouvelle note'}
        </button>
      </div>

      {open && (
        <div className="px-4 py-3 border-b border-background-200/60 bg-background-100/50 space-y-2">
          <input className={INPUT} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titre (optionnel)" />
          <textarea
            ref={bodyRef}
            className={`${INPUT} resize-none`}
            rows={3}
            maxLength={500}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Contenu de la note…"
          ></textarea>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
              {[{ key: 'internal', label: 'Interne' }, { key: 'private', label: 'Privée' }].map((v) => (
                <button
                  key={v.key}
                  type="button"
                  onClick={() => setVisibility(v.key)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer ${
                    visibility === v.key ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  {v.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <VoiceInputButton
                userId={user?.id}
                onTranscript={handleTranscript}
                title="Dicter la note"
              />
              <button
                type="button"
                onClick={add}
                disabled={saving || !body.trim()}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50"
              >
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {error && <div className="px-4 py-2.5 text-xs text-red-600 bg-red-50">{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <i className="ri-loader-4-line animate-spin text-xl text-primary-500"></i>
        </div>
      ) : notes.length === 0 ? (
        <p className="px-4 py-6 text-xs text-foreground-400 text-center">Aucune note interne.</p>
      ) : (
        <div className="divide-y divide-background-200/60">
          {notes.map((n) => {
            const vis = noteVisibilityMeta(n.visibility);
            return (
              <div key={n.id} className="px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      {n.pinned && <i className="ri-pushpin-fill text-accent-500 text-xs"></i>}
                      {n.title && <span className="text-sm font-semibold text-foreground-900">{n.title}</span>}
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${vis.className}`}>
                        <i className={vis.icon}></i>
                        {vis.label}
                      </span>
                    </div>
                    <p className="text-sm text-foreground-700 mt-1 leading-relaxed whitespace-pre-line">{n.body}</p>
                    <p className="text-[11px] text-foreground-400 mt-1">
                      {fmt(n.created_at)}
                      {n.author_name ? ` · ${n.author_name}` : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => pin(n)}
                      disabled={busyId === n.id}
                      title={n.pinned ? 'Désépingler' : 'Épingler'}
                      className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-accent-600 hover:bg-accent-50 transition-colors cursor-pointer disabled:opacity-50"
                    >
                      <i className={n.pinned ? 'ri-pushpin-fill' : 'ri-pushpin-line'}></i>
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(n)}
                      disabled={busyId === n.id}
                      title="Supprimer"
                      className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer disabled:opacity-50"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}